import sunSynchData from '../../data/sun_synch.json';

/**
 * Phase 9 chunk 3 — sun-synchronous ribbon roster loader.
 *
 * Reads the curated list from `resources/data/sun_synch.json`,
 * validates entries (drops malformed rows + dedups by NORAD), and
 * enforces a hard cap of {@link SUN_SYNCH_MAX} satellites per
 * docs/phase9.md § IV "Sun-synch ribbon perf" mitigation. The JSON
 * file is configuration; the cap is the load-bearing safety rail
 * for the Cesium polyline budget.
 *
 * Cesium-free + DOM-free so the validation lives in Vitest.
 */

export interface SunSynchEntry {
  norad: number;
  name: string;
}

/** Hard cap per § IV — 9 sats today, room for ~11 more before we hit the wall. */
export const SUN_SYNCH_MAX = 20;

/**
 * Build the operational roster from a raw JSON payload. Defensive:
 *   - drops rows that aren't `{ norad: number, name: string }`
 *   - dedups by NORAD (first occurrence wins)
 *   - caps at {@link SUN_SYNCH_MAX} entries, logging a warning if
 *     the config tries to load more (so contributors don't silently
 *     blow the Cesium budget)
 */
export function validateRoster(raw: unknown, warn: (msg: string) => void = console.warn): SunSynchEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: SunSynchEntry[] = [];
  const seen = new Set<number>();
  for (const row of raw) {
    if (
      typeof row !== 'object' || row === null
      || typeof (row as { norad?: unknown }).norad !== 'number'
      || typeof (row as { name?: unknown }).name !== 'string'
    ) continue;
    const norad = (row as { norad: number }).norad;
    const name  = (row as { name: string  }).name;
    if (!Number.isInteger(norad) || norad <= 0) continue;
    if (name.trim() === '') continue;
    if (seen.has(norad)) continue;
    seen.add(norad);
    out.push({ norad, name });
  }
  if (out.length > SUN_SYNCH_MAX) {
    warn(`[sunSynchLoader] roster has ${out.length} entries; capping at ${SUN_SYNCH_MAX} (see docs/phase9.md § IV)`);
    out.length = SUN_SYNCH_MAX;
  }
  return out;
}

/** Returns the validated, capped operational roster. */
export function loadRoster(): SunSynchEntry[] {
  return validateRoster(sunSynchData);
}

/**
 * Phase 9 chunk 3 — ribbon-segment alpha curve. Past-dimmer-than-
 * future, but scaled overall to a dimmer max than the chunk-2
 * selection ribbon (these are always-on context, not focused
 * attention).
 *
 * Returns alpha in [0, 1]. Pure-math so the falloff math lives in
 * Vitest.
 */
export const SUN_SYNCH_MAX_ALPHA = 0.4;
export const SUN_SYNCH_MIN_ALPHA = 0.04;

export function sunSynchSegmentAlpha(deltaMs: number, halfWindowMs: number): number {
  if (halfWindowMs <= 0 || !Number.isFinite(halfWindowMs)) return SUN_SYNCH_MIN_ALPHA;
  const rel = deltaMs / halfWindowMs;
  const absRel = Math.min(1, Math.abs(rel));
  let alpha = SUN_SYNCH_MAX_ALPHA - (SUN_SYNCH_MAX_ALPHA - SUN_SYNCH_MIN_ALPHA) * absRel;
  if (rel < 0) {
    // Past is dimmer than future, same shape as chunk-2 ribbons.
    alpha *= 0.6;
  }
  return Math.max(SUN_SYNCH_MIN_ALPHA, Math.min(SUN_SYNCH_MAX_ALPHA, alpha));
}
